import React from 'react';
import { isChunkLoadError } from '@lib/lazy-with-reload';
import { Alert, AlertDescription, AlertTitle } from '@ui/alert';
import { Button } from '@ui/button';
import { Home, RefreshCcw } from 'lucide-react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';

interface RouteErrorCopy {
  title: string;
  message: string;
  reloadLabel: string;
}

const describeRouteError = (error: unknown): RouteErrorCopy => {
  if (isRouteErrorResponse(error)) {
    const detail = typeof error.data?.message === 'string' ? error.data.message : null;
    return {
      title: `${error.status} ${error.statusText || 'Error'}`,
      message: error.status === 404 ? 'Page not found' : (detail ?? 'An unexpected error occurred'),
      reloadLabel: 'Try Again',
    };
  }
  if (isChunkLoadError(error)) {
    return {
      title: 'A new version of ClearOffer is available',
      message:
        'This tab was open while a new version was released, so it could not load the page. ' +
        'Reload to pick up the latest version.',
      reloadLabel: 'Reload',
    };
  }
  return {
    title: 'Something went wrong',
    message: error instanceof Error ? error.message : 'An unexpected error occurred',
    reloadLabel: 'Try Again',
  };
};

/**
 * errorElement for every route: renders whatever the route threw with a way out.
 */
export const RouteErrorBoundary: React.FC = () => {
  const error = useRouteError();
  const { title, message, reloadLabel } = describeRouteError(error);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-muted">
      <div className="w-full max-w-md">
        <Alert variant="destructive" className="mb-6 border-2 border-destructive/20">
          <AlertTitle className="text-xl font-bold mb-2" data-testid="route-error-title">
            {title}
          </AlertTitle>
          <AlertDescription className="mt-2">
            <p className="text-muted-foreground mb-4" data-testid="route-error-message">
              {message}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <Button onClick={() => window.location.reload()} data-testid="route-error-reload">
                <RefreshCcw className="h-4 w-4 mr-2" />
                {reloadLabel}
              </Button>
              <Button
                variant="outline"
                onClick={() => (window.location.href = '/')}
                data-testid="route-error-home"
              >
                <Home className="h-4 w-4 mr-2" />
                Return to Home
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
};
